/**
 * The card on the shelf, up close.
 *
 * A row click opens this over the table: the Scryfall picture, where the copy
 * lives, and the three things a person may change about it (finish, condition,
 * a note). The location itself is read-only - moving a card means filing it
 * into another bin, which is the intake route's job with its slot numbers.
 *
 * Escape, the backdrop, and the close button all leave; saving reports the
 * updated copy so the table can reload without guessing what changed.
 */

import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { CONDITIONS, displayCondition, FINISHES } from '@unsorted/shared';
import { ApiError, type Api } from '../api/http.js';
import { scryfallFallbackUrl, scryfallImageUrl } from '../api/images.js';
import type { CollectionCopy, Copy, CopyLocation } from '../api/types.js';
import { Banner } from './Banner.js';

export interface CopyModalProps {
  api: Api;
  row: CollectionCopy;
  onClose: () => void;
  onSaved: () => void;
}

const finishLabel = (finish: string): string =>
  finish === 'nonfoil' ? 'Non-foil' : finish.charAt(0).toUpperCase() + finish.slice(1);

function CardImage({ row, large }: { row: CollectionCopy; large: boolean }): ReactNode {
  const version = large ? 'large' : 'normal';
  const [source, setSource] = useState<string | null>(() => scryfallImageUrl(row, version));
  const fallback = scryfallFallbackUrl(row, version);
  if (source === null) return null;
  return (
    <img
      className={large ? 'modal-art' : 'preview-art'}
      src={source}
      alt={`Card art for ${row.name}`}
      loading="lazy"
      onError={() => {
        if (fallback !== null && source !== fallback) setSource(fallback);
        else setSource(null);
      }}
    />
  );
}

export function CopyModal({ api, row, onClose, onSaved }: CopyModalProps): ReactNode {
  const [detail, setDetail] = useState<{ copy: Copy; location: CopyLocation | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [finish, setFinish] = useState('nonfoil');
  const [condition, setCondition] = useState('near_mint');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .copy(row.publicId)
      .then((found) => {
        if (cancelled) return;
        setDetail(found);
        setFinish(found.copy.finish);
        setCondition(found.copy.condition);
        setNotes(found.copy.notes ?? '');
      })
      .catch((failure: unknown) => {
        if (!cancelled) setLoadError(failure instanceof ApiError ? failure.message : 'it could not be read');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, row.publicId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    dialogRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const dirty =
    detail !== null &&
    (finish !== detail.copy.finish || condition !== detail.copy.condition || notes !== (detail.copy.notes ?? ''));

  const save = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!dirty) return;
    setBusy(true);
    setSaveError(null);
    try {
      await api.updateCopy(row.publicId, {
        finish,
        condition,
        notes: notes === '' ? null : notes,
      });
      onSaved();
    } catch (failure) {
      setSaveError(failure instanceof ApiError ? failure.message : 'it could not be saved');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={`${row.name} details`} ref={dialogRef} tabIndex={-1}>
        <div className="modal-head">
          <div>
            <h3>
              {row.name}{' '}
              <span className="hint tnum">
                {row.setCode === '' ? '' : `${row.setCode.toUpperCase()} ${row.collectorNumber}`}
              </span>
            </h3>
            <p className="hint walk-address">{row.address}</p>
          </div>
          <button type="button" className="ghost" onClick={onClose} aria-label="Close details">
            ✕
          </button>
        </div>

        <CardImage row={row} large />

        {loading && <p className="hint">Reading the copy…</p>}
        {loadError && <Banner kind="error">{loadError}</Banner>}

        {detail && (
          <form className="modal-form" onSubmit={(event) => void save(event)}>
            <div className="row">
              <label className="grow">
                Finish
                <select value={finish} onChange={(event) => setFinish(event.target.value)}>
                  {FINISHES.map((option) => (
                    <option key={option} value={option}>
                      {finishLabel(option)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grow">
                Condition
                <select value={condition} onChange={(event) => setCondition(event.target.value)}>
                  {CONDITIONS.map((option) => (
                    <option key={option} value={option}>
                      {displayCondition(option)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label>
              Note (optional)
              <input
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="Edge nick, signed…"
                autoComplete="off"
                spellCheck={false}
                maxLength={255}
              />
            </label>
            <p className="hint">
              {detail.copy.lang === '' ? '' : `Language: ${detail.copy.lang.toUpperCase()} · `}
              Source: {detail.copy.source}
              {detail.copy.acquiredAt ? ` · Since ${detail.copy.acquiredAt}` : ''}
            </p>
            {saveError && <Banner kind="error">{saveError}</Banner>}
            <div className="row">
              <button type="submit" className="primary" disabled={busy || !dirty}>
                {busy ? 'Saving…' : 'Save changes'}
              </button>
              <button type="button" className="ghost" onClick={onClose}>
                Close
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
